import React, {useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCaretDown, faClose} from "@fortawesome/free-solid-svg-icons";

// Style
import {bp} from "../style/components/breakpoints.js";
import {voice} from "../style/components/typography.js";
import {flex, z} from "../style/components/mixins.js";
import {border, pad, radius} from "../style/components/variables.js";
import {List, ListItem, Label, SearchWrapper, Button, Inline} from "../style/components/general.js";

const typeSelectPadding = ({$pills, $compact, buttonsShown}) => {
  const iconGutter = `${pad + 15 * buttonsShown + 2}px`;
  if ($pills && $compact) {
    // Icons are position:absolute and do not widen the box; keep the same right
    // gutter as non-compact pills so labels never run under the caret/close.
    return `${pad / 3}px ${iconGutter} ${pad / 4}px ${pad / 1.5}px`;
  }
  if ($pills) {
    return `${pad / 2}px ${iconGutter} ${pad / 2}px ${pad}px`;
  }
  return `${pad / 2}px ${iconGutter} ${pad / 2}px ${pad}px`;
};

const iconWrapperTop = ({$pills, $compact}) => {
  if ($pills && $compact) return "50%";
  if ($pills) return "6px";
  return "4px";
};

const Select = ({
  label,
  options = [],
  selection,
  setSelection,
  placeholder,
  disabled = false,
  large = false,
  onRemove,
  pills = false,
  compact = false,
  color,
  inverted,
  showButtonPlaceholder,
  menuAlignEnd = false,
  selectionLabel
}) => {
  const [focus, setFocus] = useState(false);
  const [skipSet, setSkipSet] = useState(false);

  const outside = useRef(null);
  const optionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        focus &&
        (outside?.current?.contains(e.target) || outside?.current?.nextSibling?.contains(e.target))
      )
        setSkipSet(true);
      if (focus && !optionsRef?.current?.contains(e.target)) {
        setFocus(false);
        setSkipSet(false);
      }
    };

    if (outside) document.addEventListener("mousedown", handleClickOutside);

    // Cleanup
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [focus]);

  const showDropdownIcon = !disabled && options.length > 1;
  const showRemoveIcon = !!(onRemove && (selection ?? selectionLabel));

  const getButtonsShown = () => {
    if (showButtonPlaceholder === false) return 0;
    if (showButtonPlaceholder === true) return 2;
    return (showDropdownIcon ? 1 : 0) + (showRemoveIcon ? 1 : 0);
  };

  return (
    <>
      {label && <Label bold>{label.toUpperCase()}</Label>}
      <SearchWrapper
        ref={outside}
        onClick={
          disabled || onRemove
            ? undefined
            : () => {
                if (options.length > 1) {
                  if (skipSet) setSkipSet(false);
                  else setFocus(true);
                }
              }
        }>
        <TypeSelect
          disabled={disabled}
          length={options.length}
          $pills={pills}
          $compact={compact}
          color={color}
          inverted={inverted}
          buttonsShown={getButtonsShown()}>
          {(selectionLabel ?? selection ?? placeholder) || "Select..."}
          {(showDropdownIcon || showRemoveIcon) && (
            <IconWrapper fill={inverted ? color : undefined} $pills={pills} $compact={compact}>
              {showDropdownIcon && (
                <Edit
                  onClick={() => {
                    if (options.length > 1) {
                      if (skipSet) setSkipSet(false);
                      else setFocus(true);
                    }
                  }}
                  type="button"
                  padding={-1}
                  opacity={1}>
                  <Icon icon={faCaretDown} />
                </Edit>
              )}
              {showRemoveIcon && (
                <Edit onClick={onRemove} type="button">
                  <Icon icon={faClose} />
                </Edit>
              )}
            </IconWrapper>
          )}
        </TypeSelect>

        {focus ? (
          <Options large={large} ref={optionsRef} $menuAlignEnd={menuAlignEnd}>
            {options
              ?.sort((a, b) => a.name.localeCompare(b.name))
              ?.map(result => (
                <Option
                  key={`result-${result.name}`}
                  $menuAlignEnd={menuAlignEnd}
                  onClick={() => {
                    setSelection(result);
                    setFocus(false);
                    setSkipSet(false);
                  }}
                  selected={selection === result.name}>
                  {result.label}
                </Option>
              ))}
          </Options>
        ) : (
          ""
        )}
      </SearchWrapper>
    </>
  );
};

Select.propTypes = {
  label: PropTypes.string,
  selection: PropTypes.string,
  setSelection: PropTypes.func.isRequired,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  large: PropTypes.bool,
  compact: PropTypes.bool,
  disabled: PropTypes.bool,
  onRemove: PropTypes.func,
  pills: PropTypes.bool,
  color: PropTypes.string,
  inverted: PropTypes.bool,
  showButtonPlaceholder: PropTypes.bool,
  menuAlignEnd: PropTypes.bool,
  selectionLabel: PropTypes.string
};

// Style Overrides
const Options = styled(List)`
  position: absolute;
  top: calc(100% - 5px);
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: start;
  width: min-content;
  min-width: ${({large}) => (large ? "210px" : "155px")};
  margin-top: ${pad}px;
  background: ${({theme}) => theme.tertiary};
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  box-shadow: 0 3px 5px rgb(0, 0, 0, 0.5);
  overflow: hidden;
  z-index: ${z("higher")};
  overflow: hidden;

  ${({$menuAlignEnd}) =>
    $menuAlignEnd &&
    css`
      right: 0;
      left: unset;
      align-items: stretch;
    `}

  ${bp(2)} {
    right: 0;
    left: unset;
  }
`;

const Option = styled(ListItem)`
  ${voice.small}
  position: relative;
  display: inline-block;
  width: 100%;
  padding: ${pad / 2}px ${pad}px;
  white-space: nowrap;
  border: ${border} solid ${({theme}) => theme.tertiary};
  text-align: ${({$menuAlignEnd}) => ($menuAlignEnd ? "right" : "center")};

  &:hover {
    color: ${({theme}) => theme.tertiary};
    background: ${({theme}) => theme.secondary};
    border: ${border} solid ${({theme}) => theme.secondary};

    ${Button} {
      color: ${({theme}) => theme.tertiary};
    }
  }

  ${({selected}) =>
    selected &&
    css`
      color: ${({theme}) => theme.tertiary};
      background: ${({theme}) => theme.secondary};
      border: ${border} solid ${({theme}) => theme.secondary};

      ${Button} {
        color: ${({theme}) => theme.tertiary};
      }
    `}

  ${Button} {
    width: 100%;
    text-align: ${({$menuAlignEnd}) => ($menuAlignEnd ? "right" : "left")};
    padding: 0;
    background: 0;
    color: ${({theme}) => theme.secondary};
  }
`;

const TypeSelect = styled.div`
  ${voice.small};
  ${flex("row", "nowrap")}
  white-space: nowrap;
  font-weight: bold;
  border-radius: ${pad * 3}px;
  position: relative;
  align-items: center;
  background: ${({theme, color, inverted}) => (inverted ? theme.tertiary : color || theme.primary)};
  color: ${({theme, color, inverted}) => (inverted ? color || theme.primary : theme.tertiary)};
  padding: ${typeSelectPadding};

  ${({inverted, theme, color}) =>
    inverted !== undefined &&
    css`
      border: 2px solid ${color || theme.primary};
      line-height: 1.5em;
      cursor: ${!inverted ? "auto" : "pointer"};
    `}

  text-overflow: ellipsis;
  text-align: center;

  ${({disabled, inverted}) =>
    disabled &&
    inverted === undefined &&
    css`
      -webkit-appearance: none;
      -moz-appearance: none;
      text-indent: 0.01px;
      text-overflow: "";
    `}

  ${({length, inverted}) =>
    length > 1 &&
    inverted === undefined &&
    css`
      appearance: none;
      text-indent: 1px;
    `}

  ${({length}) =>
    length === 1 &&
    css`
      pointer-events: none;
    `}
`;

const Icon = styled(FontAwesomeIcon)`
  position: relative;
  padding: 0 4px;
`;

const IconWrapper = styled(Inline)`
  position: absolute;
  right: 4px;
  top: ${iconWrapperTop};
  transform: ${({$pills, $compact}) => ($pills && $compact ? "translateY(-50%)" : "none")};
  max-height: 100%;
  gap: 0;

  svg {
    fill: ${({theme, fill}) => fill || theme.tertiary};
  }
`;

const Edit = styled.button`
  font-size: unset;
  margin-top: ${({padding}) => padding}px;

  ${({opacity}) =>
    opacity !== undefined &&
    css`
      opacity: ${opacity};
    `}
`;

export default Select;
